<script setup lang="ts">
import type { PlatformSaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
    asset: PlatformSaleItemEntity['mainAsset'];
}

const { sale, asset } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { trackGalleryView, attachSdpVideoTracker } = useTracking();
const { saleImageAlt, saleVideoAlt } = useA11y();
const { getVideoSrc } = useVideoSrc();

// CONSTANTS
// Both slide kinds must occupy the exact same box, so the gallery keeps its height across the photo/video boundary.
const SLIDE_CLASS = 'aspect-square w-full rounded-xl border lg:aspect-4/3';
const ACTIVE_PILL_CLASS = 'bg-default ring-primary hover:bg-primary-50 active:bg-primary-50 ring-2';
// Matches the variant the carousel gives its own arrows. The play chip is a bare span with nothing to
// inherit, so it restates the recipe.
const CHROME_CLASS = 'bg-default text-default ring ring-inset ring-accented';

// TYPES
type GallerySlide = { kind: 'image'; src: string } | { kind: 'video'; src: string; labelKey: TranslationKey | undefined; videoIndex: number };

// DATA
const mediaCarousel = useTemplateRef('mediaCarousel');
const imageLightboxOpen = ref<boolean>(false);
const videoLightboxOpen = ref<boolean>(false);
const slideIndex = ref<number>(0);
const videoElementRefs = ref<(HTMLVideoElement | null)[]>([]);
const videoTrackerCleanupFns = ref<VoidFunction[]>([]);
const startedVideos = ref<boolean[]>([]);

const baseImages = computed<string[]>(() => asset.images.base);

const videos = computed<string[]>(() => {
    const { engine, gearbox, other } = asset.videos;

    return [...engine, ...gearbox, ...other];
});

// Photos come first, so a photo's slide index is also its index in the image lightbox; a video carries its own index for the video lightbox.
const slides = computed<GallerySlide[]>(() => [
    ...baseImages.value.map((src): GallerySlide => ({ kind: 'image', src })),
    ...videos.value.map((src, videoIndex): GallerySlide => ({ kind: 'video', src, labelKey: saleVideoLabelKey(src), videoIndex })),
]);

const isVideoSlide = computed<boolean>(() => slideIndex.value >= baseImages.value.length);
const videoSlideIndex = computed<number>(() => slideIndex.value - baseImages.value.length);

const counterLabel = computed<string>(() =>
    isVideoSlide.value ? `${videoSlideIndex.value + 1}/${videos.value.length}` : `${slideIndex.value + 1}/${baseImages.value.length}`
);

// A lightbox is only ever opened from a slide of its own kind, but both stay mounted while the other kind
// is on screen, so clamp the index each one is handed.
const imageInitialSlide = computed<number>(() => Math.min(slideIndex.value, Math.max(0, baseImages.value.length - 1)));
const videoInitialSlide = computed<number>(() => Math.max(0, videoSlideIndex.value));

// METHODS
const setVideoRef = (el: Element | ComponentPublicInstance | null, videoIndex: number): void => {
    if (!(el instanceof HTMLVideoElement)) {
        videoTrackerCleanupFns.value?.[videoIndex]?.();
        videoElementRefs.value[videoIndex] = null;

        return;
    }

    if (videoElementRefs.value[videoIndex] === el) return;

    videoElementRefs.value[videoIndex] = el;
    videoTrackerCleanupFns.value[videoIndex] = attachSdpVideoTracker(el);
};

const pauseVideo = (videoIndex: number): void => {
    videoElementRefs.value[videoIndex]?.pause();
};

// HANDLERS
const onCarouselSelect = (index: number): void => {
    // Embla re-emits the current slide on every resize-driven reInit; only a real change may pause.
    if (index === slideIndex.value) return;

    // Reads the outgoing slide: two audio streams must never overlap.
    if (isVideoSlide.value) {
        pauseVideo(videoSlideIndex.value);
    }

    slideIndex.value = index;
};

const scrollTo = (index: number): void => {
    mediaCarousel.value?.emblaApi?.scrollTo(index);
};

const onVideoLoadedMetadata = (event: Event): void => {
    // There are no poster images in the pipeline, so nudge off zero to make the browser paint a real frame.
    const video = event.target;

    if (video instanceof HTMLVideoElement && video.currentTime === 0) {
        video.currentTime = 0.1;
    }
};

const onVideoPlay = (videoIndex: number): void => {
    startedVideos.value[videoIndex] = true;
};

const playVideo = (videoIndex: number): void => {
    // A rejected play() is normal (interrupted load, autoplay policy) and must not surface as an unhandled rejection.
    videoElementRefs.value[videoIndex]?.play().catch(() => undefined);
};

const openImageLightbox = (): void => {
    imageLightboxOpen.value = true;
    trackGalleryView('photo', 'main');
};

const openVideoLightbox = (): void => {
    pauseVideo(videoSlideIndex.value);
    videoLightboxOpen.value = true;
    trackGalleryView('video', 'main');
};

const openLightbox = (): void => {
    if (isVideoSlide.value) {
        openVideoLightbox();
    } else {
        openImageLightbox();
    }
};

const onImageLightboxIndexChange = (index: number): void => {
    scrollTo(index);
};

const onVideoLightboxIndexChange = (index: number): void => {
    scrollTo(baseImages.value.length + index);
};

// LIFECYCLE
onUnmounted(() => {
    videoTrackerCleanupFns.value.forEach(cleanupFn => cleanupFn?.());
});
</script>

<template>
    <div>
        <div v-if="slides.length" class="relative">
            <UCarousel
                ref="mediaCarousel"
                v-slot="{ item, index }"
                :items="slides"
                arrows
                loop
                :ui="{
                    item: 'basis-full',
                    prev: 'z-10 start-0 -translate-x-1/2 sm:start-0 md:start-4 md:translate-x-0',
                    next: 'z-10 end-0 translate-x-1/2 sm:end-0 md:end-4 md:translate-x-0',
                }"
                @select="onCarouselSelect"
            >
                <NuxtImg
                    v-if="item.kind === 'image'"
                    class="cursor-grab object-cover active:cursor-grabbing lg:cursor-zoom-in"
                    :class="SLIDE_CLASS"
                    :src="item.src"
                    :width="1200"
                    densities="x1"
                    :loading="index === 0 ? 'eager' : 'lazy'"
                    :preload="index === 0"
                    :draggable="false"
                    :alt="saleImageAlt(asset, item.src)"
                    @click="openImageLightbox"
                />
                <div v-else class="relative flex items-center justify-center overflow-hidden bg-black" :class="SLIDE_CLASS">
                    <video
                        :ref="(el: Element | ComponentPublicInstance | null) => setVideoRef(el, item.videoIndex)"
                        class="h-full w-full object-contain"
                        :src="getVideoSrc(item.src)"
                        :preload="index === slideIndex ? 'metadata' : 'none'"
                        :controls="startedVideos[item.videoIndex]"
                        playsinline
                        :aria-label="saleVideoAlt(asset, item.src)"
                        @loadedmetadata="onVideoLoadedMetadata"
                        @play="onVideoPlay(item.videoIndex)"
                        @mousedown.stop
                        @touchstart.stop.passive
                        @keydown.stop
                    />

                    <!-- Equal outer rows pin the play chip to the exact centre; the label hangs below it. -->
                    <button
                        v-if="!startedVideos[item.videoIndex]"
                        type="button"
                        class="absolute inset-0 grid cursor-pointer grid-rows-[1fr_auto_1fr] justify-items-center bg-black/40 transition-colors hover:bg-black/25"
                        :aria-label="tt('a11y.playVideo')"
                        @click="playVideo(item.videoIndex)"
                    >
                        <span class="row-start-2 flex size-14 items-center justify-center rounded-full md:size-16" :class="CHROME_CLASS">
                            <UIcon name="lucide:play" class="size-6 md:size-7" />
                        </span>
                        <UBadge
                            v-if="item.labelKey"
                            color="neutral"
                            variant="outline"
                            size="lg"
                            class="row-start-3 mt-3 self-start"
                            :label="tt(item.labelKey)"
                        />
                    </button>
                </div>
            </UCarousel>

            <UBadge color="neutral" variant="outline" size="lg" class="absolute top-2 left-2 z-10 md:top-4 md:left-4" :label="counterLabel" />
            <UButton
                color="neutral"
                variant="outline"
                size="md"
                icon="lucide:expand"
                class="absolute top-2 right-2 z-10 md:top-4 md:right-4"
                :aria-label="tt(isVideoSlide ? 'a11y.expandVideo' : 'a11y.expandImage')"
                @click="openLightbox"
            />
            <SaleTrustReportInspectionBadge v-if="!isVideoSlide" class="absolute bottom-7 left-2 z-10 md:bottom-8 md:left-4" :sale="sale" />
        </div>

        <div v-if="slides.length" class="relative z-10 -mt-4.5 flex justify-center gap-2">
            <UButton
                v-if="baseImages.length"
                :color="isVideoSlide ? 'neutral' : 'primary'"
                variant="outline"
                size="lg"
                icon="lucide:images"
                :class="isVideoSlide ? undefined : ACTIVE_PILL_CLASS"
                :label="tt('sdp.photoCount', baseImages.length)"
                @click="scrollTo(0)"
            />
            <UButton
                v-if="videos.length"
                :color="isVideoSlide ? 'primary' : 'neutral'"
                variant="outline"
                size="lg"
                icon="lucide:square-play"
                :class="isVideoSlide ? ACTIVE_PILL_CLASS : undefined"
                :label="tt('sdp.videoCount', videos.length)"
                @click="scrollTo(baseImages.length)"
            />
        </div>

        <LazySdpLightboxImage
            v-if="baseImages.length"
            v-model="imageLightboxOpen"
            :items="baseImages"
            :initial-slide="imageInitialSlide"
            @active-index-change="onImageLightboxIndexChange"
        />
        <LazySdpLightboxVideo
            v-if="videos.length"
            v-model="videoLightboxOpen"
            :main-asset="asset"
            :initial-slide="videoInitialSlide"
            @active-index-change="onVideoLightboxIndexChange"
        />
    </div>
</template>
